import React from 'react';
import { Link } from 'react-router-dom';
import { motion, type Variants } from 'framer-motion';

import { AnimatedCounter } from '../ui/AnimatedCounter';
import Kilimanjaro3D from '../components/Kilimanjaro3D';

const MotionLink = motion.create(Link);

const heroContainer: Variants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.14, delayChildren: 0.1 },
  },
};

const heroItem: Variants = {
  hidden: { opacity: 0, y: 22 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const statsContainer: Variants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12 },
  },
};

const statCard: Variants = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const stats = [
  { value: 76, label: 'Agents engaged', detail: 'International travel advisors partnering through our B2B network' },
  { value: 18, label: 'Local operators onboarded', detail: 'Community-led teams across East Africa working through verified frameworks' },
  { value: 162, label: 'Guides & hosts verified', detail: 'Local guides, conservation educators, and cultural hosts supported through our operator network' },
  { value: 6, label: 'Impact initiatives active', detail: 'Education, habitat restoration, and community-driven projects monitored through our oversight team' },
];

export default function HomePage() {
  return (
    <div className="homePage">
      <section className="homeHero">
        <motion.div
          className="homeHeroScene"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
        >
          <Kilimanjaro3D />
        </motion.div>
        <div className="homeHeroOverlay" />
        <motion.div
          className="container homeHeroContent"
          variants={heroContainer}
          initial="hidden"
          animate="visible"
        >
          <motion.h1 className="homeTitle homeTitleCloth" data-text="Rafiki" variants={heroItem}>
            Rafiki
          </motion.h1>

          <motion.div className="homeIntro" variants={heroItem}>
            <span className="homeIntroText">Power to the People</span>
          </motion.div>

          <motion.p
            className="muted"
            style={{ maxWidth: 640, fontSize: '1.05rem', lineHeight: 1.6 }}
            variants={heroItem}
          >
            SafariCo Hub is a non-profit association turning East African safaris into shared
            prosperity — for the guides, operators, and communities who make every journey possible.
          </motion.p>

          <motion.div className="homeHeroButtons" variants={heroItem}>
            <MotionLink
              className="btn btnPrimary"
              to="/operators"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
            >
              Explore verified operators
            </MotionLink>

            <MotionLink
              to="/donations"
              className="headerCTA"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
            >
              Support Impact
            </MotionLink>
          </motion.div>
        </motion.div>
      </section>

      <section className="homeBand homeBandAlt">
        <div className="container">
          <motion.div
            className="homeSectionHeader"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <div className="kicker">Analytics</div>
            <h2 className="sectionTitle">Our network at a glance</h2>
          </motion.div>

          <motion.div
            className="analyticsGrid"
            variants={statsContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            {stats.map((stat) => (
              <motion.div
                key={stat.label}
                className="statCard"
                variants={statCard}
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <div className="statValue">
                  <AnimatedCounter to={stat.value} />
                </div>
                <div className="statLabel">{stat.label}</div>
                <div className="statDetail">{stat.detail}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
